import axios from "axios";
import { DataTable } from "simple-datatables";
import { showAlert, showConfirm, handleRequestError } from "../../utils/alert";
import VMasker from "vanilla-masker";

export default () => ({
  // Modal States
  isModalOpen: false,
  isSubmitting: false,

  // Edit States
  editMode: false,
  editId: null,

  // Data Storage
  clientsList: [],

  // Form Payload
  form: {
    name: "",
    phone: "",
    push_optin: false,
  },

  // DataTable Instance
  dataTableInstance: null,

  async initClients() {
    const token = localStorage.getItem("jwt_token");
    if (!token) {
      window.location.href = "/client/login";
      return;
    }

    window.axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    await this.loadTable();
  },

  // ================= FORMATTING HELPERS =================

  applyPhoneMask(value) {
    if (!value) return "";
    const onlyNumbers = value.toString().replace(/\D/g, "");
    return VMasker.toPattern(onlyNumbers, "(99) 99999-9999");
  },

  handlePhoneInput(event) {
    this.form.phone = this.applyPhoneMask(event.target.value);
  },

  formatDate(dateString) {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR");
  },

  // ================= DATA TABLE =================

  async loadTable() {
    try {
      const response = await axios.get("/api/client/clients");
      this.clientsList = response.data.data;

      if (this.dataTableInstance) {
        this.dataTableInstance.destroy();
      }

      const tbody = document.querySelector("#clientsTable tbody");
      tbody.innerHTML = this.clientsList
        .map((client) => {
          const phoneDisplay = this.applyPhoneMask(client.phone);

          // Badge visual de status do WhatsApp
          const optinBadge = client.push_optin
            ? `<span class="inline-flex items-center gap-1 mt-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-bold text-green-700 uppercase tracking-wide"><svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg> Autorizado</span>`
            : `<span class="inline-flex items-center gap-1 mt-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500 uppercase tracking-wide"><svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/></svg> Não Autorizado</span>`;

          return `
            <tr class="hover:bg-slate-50/50 transition-colors">
              <td class="py-4 px-4 xl:px-6">
                <p class="font-bold text-slate-800">${client.name}</p>
              </td>
              <td class="py-4 px-4 xl:px-6 font-medium text-slate-700 font-mono">
                ${phoneDisplay}
                <div class="block">${optinBadge}</div>
              </td>
              <td class="py-4 px-4 xl:px-6 text-slate-500 text-sm">
                ${this.formatDate(client.created_at)}
              </td>
              <td class="text-left py-4 px-4 xl:px-6">
                <div class="flex justify-start gap-2">
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('edit-client', {detail: ${client.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    Editar
                  </button>
                  <button
                    onclick="window.dispatchEvent(new CustomEvent('delete-client', {detail: ${client.id}}))"
                    class="inline-flex items-center justify-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-600"
                  >
                    Excluir
                  </button>
                </div>
              </td>
            </tr>
          `;
        })
        .join("");

      this.dataTableInstance = new DataTable("#clientsTable", {
        searchable: false,
        perPageSelect: false,
        labels: {
          info: "Mostrando de {start} até {end} de {rows} clientes",
          noRows: "Nenhum cliente cadastrado",
          noResults: "Nenhum cliente encontrado na busca",
          searchTitle: "Buscar cliente...",
        },
      });
    } catch (error) {
      console.error(error);
    }
  },

  // ================= MODAL HANDLERS =================

  openCreate() {
    this.editMode = false;
    this.form = {
      name: "",
      phone: "",
      push_optin: false,
    };
    this.isModalOpen = true;
  },

  openEdit(id) {
    this.editMode = true;
    this.editId = id;
    const client = this.clientsList.find((c) => c.id === id);

    this.form = {
      name: client.name,
      phone: this.applyPhoneMask(client.phone),
      push_optin: Boolean(client.push_optin),
    };
    this.isModalOpen = true;
  },

  // ================= SUBMIT =================

  async submitForm() {
    this.isSubmitting = true;
    try {
      if (this.editMode) {
        await axios.put(`/api/client/clients/${this.editId}`, this.form);
        showAlert("Cliente atualizado com sucesso!", "success");
      } else {
        await axios.post("/api/client/clients", this.form);
        showAlert("Cliente cadastrado com sucesso!", "success");
      }

      await this.loadTable();
      this.isModalOpen = false;
    } catch (error) {
      // Se a API retornar erro de duplicidade (422), mostramos o alerta exato.
      if (
        error.response &&
        error.response.status === 422 &&
        error.response.data.error
      ) {
        showAlert(error.response.data.error, "warning");
      } else {
        handleRequestError(error, "Erro ao guardar o cliente.");
      }
    } finally {
      this.isSubmitting = false;
    }
  },

  // ================= DELETE =================

  async deleteRecord(id) {
    const result = await showConfirm(
      "Excluir Cliente",
      "Tem certeza? O histórico de vendas deste cliente será mantido (como Venda de Balcão), mas o cadastro será apagado.",
    );

    if (result.isConfirmed) {
      try {
        await axios.delete(`/api/client/clients/${id}`);
        await this.loadTable();
        showAlert("Cliente removido com sucesso.", "success");
      } catch (error) {
        handleRequestError(error, "Erro ao excluir o cliente.");
      }
    }
  },
});
